/**
 * Storage Engine — Phase 0/1: KV über localStorage oder IndexedDB (Dexie).
 * Session weiterhin sessionStorage. Sync-API für bestehende Aufrufer.
 * @see docs/adr/ADR-0002-storage-engine.md
 */

import { deserializeFromKv, serializeForKv } from './JsonSerializer';

const COLLECTION_KV = 'kv';
const COLLECTION_SESSION = 'session';

function getDefaultBackend() {
  if (typeof window === 'undefined' || !window.localStorage) {
    const mem = new Map();
    return {
      getItem: (k) => (mem.has(k) ? mem.get(k) : null),
      setItem: (k, v) => {
        mem.set(k, String(v));
      },
      removeItem: (k) => {
        mem.delete(k);
      },
      key: (i) => Array.from(mem.keys())[i] ?? null,
      get length() {
        return mem.size;
      }
    };
  }
  return window.localStorage;
}

function getDefaultSessionBackend() {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return getDefaultBackend();
  }
  return window.sessionStorage;
}

export class StorageEngine {
  /**
   * @param {{ getItem: Function, setItem: Function, removeItem: Function, key?: Function, length?: number }} [backend]
   * @param {{ getItem: Function, setItem: Function, removeItem: Function }} [sessionBackend]
   */
  constructor(backend = getDefaultBackend(), sessionBackend = getDefaultSessionBackend()) {
    this._backend = backend;
    this._sessionBackend = sessionBackend;
    /** @type {Map<string, Promise<unknown>>} */
    this._queues = new Map();
  }

  /**
   * @param {string} collection — nur 'kv' in Phase 0
   * @param {string} id — localStorage-Key
   * @returns {unknown|undefined}
   */
  get(collection, id) {
    const raw = this._rawGet(collection, id);
    if (raw === null) return undefined;
    return deserializeFromKv(raw);
  }

  /**
   * Rohwert aus localStorage/sessionStorage (ohne JSON-Parse).
   * @param {string} collection
   * @param {string} id
   * @returns {string|null}
   */
  getRaw(collection, id) {
    this._assertCollection(collection);
    return this._rawGetBackend(collection).getItem(id);
  }

  /**
   * Rohstring schreiben (für bit-genaue Verifikation, z. B. Auto-Save).
   */
  putRaw(collection, id, rawString) {
    this._assertCollection(collection);
    this._rawGetBackend(collection).setItem(id, rawString);
  }

  /** Alle Keys der KV-Backend-Instanz. */
  listKvKeys() {
    const b = this._backend;
    if (b && typeof b.listKeys === 'function') {
      return b.listKeys();
    }
    const keys = [];
    for (let i = 0; i < b.length; i++) {
      const k = b.key(i);
      if (k) keys.push(k);
    }
    return keys;
  }

  /**
   * @param {string} collection
   * @param {string} id
   * @param {unknown} entity — Objekte werden JSON-serialisiert, string roh
   */
  put(collection, id, entity) {
    this._assertCollection(collection);
    this._rawGetBackend(collection).setItem(id, serializeForKv(entity));
  }

  /**
   * Schreibt und wartet auf IndexedDB-Persistenz (Stromausfall-sicher).
   * @param {string} collection
   * @param {string} id
   * @param {unknown} entity
   */
  async putPersisted(collection, id, entity) {
    this._assertCollection(collection);
    const raw = serializeForKv(entity);
    const backend = this._rawGetBackend(collection);
    if (typeof backend?.setItemAndAwait === 'function') {
      await backend.setItemAndAwait(id, raw);
    } else {
      backend.setItem(id, raw);
      if (typeof backend?.flushPendingWrites === 'function') {
        await backend.flushPendingWrites();
      }
    }
  }

  /** @param {string} collection @param {string} id @param {string} rawString */
  async putRawPersisted(collection, id, rawString) {
    this._assertCollection(collection);
    const backend = this._rawGetBackend(collection);
    if (typeof backend?.setItemAndAwait === 'function') {
      await backend.setItemAndAwait(id, rawString);
    } else {
      backend.setItem(id, rawString);
      if (typeof backend?.flushPendingWrites === 'function') {
        await backend.flushPendingWrites();
      }
    }
  }

  bulkPut(collection, entries) {
    if (collection !== COLLECTION_KV) {
      throw new Error('StorageEngine.bulkPut: nur "kv" in Phase 0');
    }
    for (const { id, entity } of entries) {
      this.put(collection, id, entity);
    }
  }

  delete(collection, id) {
    this._assertCollection(collection);
    this._rawGetBackend(collection).removeItem(id);
  }

  /**
   * Löscht und wartet auf IndexedDB-Persistenz (falls Backend async ist).
   * @param {string} collection
   * @param {string} id
   */
  async deletePersisted(collection, id) {
    this.delete(collection, id);
    const backend = this._rawGetBackend(collection);
    if (typeof backend?.removeItemAndAwait === 'function') {
      await backend.removeItemAndAwait(id);
    } else if (typeof backend?.flushPendingDeletes === 'function') {
      await backend.flushPendingDeletes();
    }
  }

  /** @returns {Promise<unknown>} */
  query() {
    return Promise.reject(new Error('StorageEngine.query: noch nicht implementiert (Phase IndexedDB).'));
  }

  /**
   * Serialisierte Ausführung pro Ressource (Queue, ADR-0003).
   * @template T
   * @param {string} resourceKey
   * @param {() => Promise<T> | T} fn
   * @returns {Promise<T>}
   */
  async acquireLock(resourceKey, fn) {
    const prev = this._queues.get(resourceKey) || Promise.resolve();
    const run = prev.then(
      () => fn(),
      () => fn()
    );
    this._queues.set(
      resourceKey,
      run.then(
        () => undefined,
        () => undefined
      )
    );
    return run;
  }

  releaseLock() {
    /* noop — queue-basiert */
  }

  getVersion() {
    return this._backend?.isReady?.() ? 1 : 0;
  }

  snapshot() {
    throw new Error('StorageEngine.snapshot: Phase IndexedDB / ADR-0012');
  }

  restore() {
    throw new Error('StorageEngine.restore: Phase IndexedDB / ADR-0012');
  }

  /**
   * @template T
   * @param {(tx: StorageEngine) => Promise<T> | T} fn — Phase 0: tx ist this
   */
  async transaction(fn) {
    if (this._backend?.runInTransaction) {
      return this._backend.runInTransaction(() => fn(this));
    }
    return fn(this);
  }

  /** IndexedDB-Backend aktiv (Phase 1). */
  usesIndexedDb() {
    return Boolean(this._backend?.isReady?.());
  }

  _rawGet(collection, id) {
    this._assertCollection(collection);
    return this._rawGetBackend(collection).getItem(id);
  }

  _rawGetBackend(collection) {
    if (collection === COLLECTION_SESSION) return this._sessionBackend;
    return this._backend;
  }

  _assertCollection(collection) {
    if (collection !== COLLECTION_KV && collection !== COLLECTION_SESSION) {
      throw new Error(
        `StorageEngine: unsupported collection "${collection}" (Phase 0: "${COLLECTION_KV}", "${COLLECTION_SESSION}")`
      );
    }
  }
}

export const KV_COLLECTION = COLLECTION_KV;
export const SESSION_COLLECTION = COLLECTION_SESSION;
